import { Organization } from "types/profile"
import { Box } from "@mui/system"
import React, { useEffect, useState } from "react"
import { Chip } from "@oreve/business-ui"
import {
    Typography,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    Avatar,
    Divider,
    Badge,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { ChevronRight } from "@mui/icons-material"
import { useUserContext } from "context/UserContext.tsx"
import { Loader } from "components/Loader.tsx"

const cleanPartnerName = (name?: string): string => {
    if (!name) return ""
    return name.replace(/^\s*(?:\[[^]]*]\s*)+/, "").trim()
}

interface OrganizationTagProps {
    CurrentOrganization: Organization | null
    mode?: "chip" | "list"
    onClick?: (organizationId: string) => void
}

export const OrganizationTag: React.FC<OrganizationTagProps> = ({
    CurrentOrganization,
    mode,
    onClick,
}) => {
    if (!CurrentOrganization) return <Loader />
    switch (mode) {
        case "list":
            return (
                <OrganizationList
                    CurrentOrganization={CurrentOrganization}
                    onClick={onClick}
                />
            )
        case "chip":
            return (
                <OrganizationChip CurrentOrganization={CurrentOrganization} />
            )
        default:
            return (
                <OrganizationChip CurrentOrganization={CurrentOrganization} />
            )
    }
}

const OrganizationChip: React.FC<OrganizationTagProps> = ({
    CurrentOrganization,
    mode,
}) => {
    const displayName = cleanPartnerName(CurrentOrganization?.name)
    const initials = displayName.substring(0, 3).toUpperCase()

    return (
        <Box sx={{ marginTop: "0.5rem" }}>
            <Chip
                data-testid="entity-chip"
                avatar={
                    <Avatar
                        sx={{
                            bgcolor: "primary.main",
                            width: 24,
                            height: 24,
                            fontSize: "0.70rem",
                        }}
                    >
                        <Typography
                            sx={{
                                color: "white",
                            }}
                            variant={"avatar"}
                        >
                            {initials}
                        </Typography>
                    </Avatar>
                }
                label={
                    <Typography variant="labelLarge">{displayName}</Typography>
                }
                sx={{
                    backgroundColor: "white",
                    height: "2rem",
                    borderRadius: "2.5rem",
                }}
            />
            {CurrentOrganization?.children &&
                CurrentOrganization?.children.length > 0 && (
                    <Box sx={{ marginLeft: "3.5rem" }}>
                        {CurrentOrganization.children.map((child) => (
                            <OrganizationTag
                                key={child.id}
                                CurrentOrganization={child}
                                mode={mode}
                            />
                        ))}
                    </Box>
                )}
        </Box>
    )
}

const StyledBadge = styled(Badge)(({ theme }) => ({
    "& .MuiBadge-badge": {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.main,
        boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    },
}))

interface OrganizationListProps {
    CurrentOrganization: Organization
    mode?: "chip" | "list"
    onClick?: (organizationId: string) => void
    level?: number
    index?: number
}

const OrganizationList: React.FC<OrganizationListProps> = ({
    CurrentOrganization,
    mode,
    onClick,
    level = 0,
    index = 0,
}) => {
    const { emsp_partner_id } = useUserContext()
    const [open, setOpen] = useState(false)
    const [currentSelectedOrganization, setCurrentSelectedOrganization] =
        useState<string>(
            (typeof window !== "undefined"
                ? sessionStorage.getItem("organizationId")
                : null) ??
                emsp_partner_id ??
                null
        )

    const hasChildren =
        CurrentOrganization?.children && CurrentOrganization.children.length > 0

    const displayName = cleanPartnerName(CurrentOrganization?.name)
    const initials = displayName
        ? displayName.substring(0, 3).toUpperCase()
        : ""

    const handleRowClick = () => {
        try {
            sessionStorage.setItem("organizationId", CurrentOrganization.id)
        } catch {
            // Doesn't find sessionStorage,
        }
        setCurrentSelectedOrganization(CurrentOrganization.id)
        try {
            if (
                typeof window !== "undefined" &&
                typeof CustomEvent !== "undefined"
            ) {
                const evt = new CustomEvent("organizationIdChanged", {
                    detail: CurrentOrganization.id,
                })
                window.dispatchEvent(evt)
            }
        } catch {
            // ignore
        }
        if (onClick) onClick(CurrentOrganization.id)
    }

    useEffect(() => {
        if (typeof window === "undefined") return

        const storageHandler = () => {
            try {
                const id = sessionStorage.getItem("organizationId")
                setCurrentSelectedOrganization(id ?? emsp_partner_id ?? null)
            } catch {
                setCurrentSelectedOrganization(emsp_partner_id ?? null)
            }
        }
        const sameTabHandler = (event: Event) => {
            if (event instanceof CustomEvent) {
                const detailId =
                    typeof event.detail === "string" ? event.detail : undefined
                if (detailId) {
                    setCurrentSelectedOrganization(detailId)
                    return
                }
            }
            storageHandler()
        }

        window.addEventListener("storage", storageHandler)
        window.addEventListener("organizationIdChanged", sameTabHandler)
        return () => {
            window.removeEventListener("storage", storageHandler)
            window.removeEventListener("organizationIdChanged", sameTabHandler)
        }
    }, [emsp_partner_id])

    return (
        <List
            sx={{
                bgcolor: "white",
                borderRadius: "4px",
                height: "auto",
                width: "100%",
                paddingY: 0,
            }}
        >
            <ListItemButton
                data-cy={`header--organization__item-${level / 2}-${index}`}
                onClick={handleRowClick}
                sx={{ pl: level * 2 }}
            >
                <ListItemIcon>
                    {CurrentOrganization.id == currentSelectedOrganization ? (
                        <StyledBadge
                            overlap="circular"
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            variant="dot"
                        >
                            <Avatar
                                sx={{
                                    bgcolor: "primary.main",
                                    fontSize: "0.70rem",
                                }}
                            >
                                <Typography
                                    sx={{ color: "white" }}
                                    variant={"labelLarge"}
                                >
                                    {initials}
                                </Typography>
                            </Avatar>
                        </StyledBadge>
                    ) : (
                        <Avatar
                            sx={{
                                bgcolor: "primary.main",
                                fontSize: "0.70rem",
                            }}
                        >
                            <Typography
                                sx={{ color: "white" }}
                                variant={"labelLarge"}
                            >
                                {initials}
                            </Typography>
                        </Avatar>
                    )}
                </ListItemIcon>

                <ListItemText
                    primary={
                        <Typography variant="titleSmall">
                            {displayName}
                        </Typography>
                    }
                />

                {hasChildren && (
                    <ChevronRight
                        data-cy={`header--organization__chevron-${level / 2}-${index}`}
                        onClick={(e) => {
                            e.stopPropagation()
                            setOpen((prev) => !prev)
                        }}
                        sx={{
                            cursor: "pointer",
                            transition: "transform 0.5s",
                            transform: open ? "rotate(90deg)" : "rotate(0deg)",
                        }}
                    />
                )}
            </ListItemButton>
            <Divider
                sx={{
                    width: `calc(100% + ${level * 2 + 0.75 * 2}rem)`,
                    ml: `-${level * 2 + 0.75}rem`,
                }}
            />
            {hasChildren && (
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <List disablePadding>
                        {CurrentOrganization.children!.map((child, index) => (
                            <OrganizationList
                                key={child.id}
                                CurrentOrganization={child}
                                mode={mode}
                                level={level + 2}
                                onClick={onClick}
                                index={index}
                            />
                        ))}
                    </List>
                </Collapse>
            )}
        </List>
    )
}
